import { ReactElement, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { LinkButton, PrimaryButton, Select, SnackbarStatus, useSnackbar } from '@weareopenr/component-library';
import { useMenu } from 'src/hooks/useMenu/useMenu';
import { QueryKeys } from 'src/hooks/QueryKeys';
import { moveMenuItemToSection } from 'src/api/moveMenuItemToSection/moveMenuItemToSection';
import { getHierarchyNames } from 'src/helpers';
import { ButtonContainer } from 'src/components/ButtonContainer/ButtonContainer';
import { useReportTrayDirty } from 'src/contexts/menuTray';
import { flattenCategoryHierarchy } from './utilities/flattenCategoryHierarchy/flattenCategoryHierarchy';
import styles from './MoveCategoryTrayContent.module.scss';

export interface MoveCategoryTrayContentProps {
  menuId: string;
  itemId: string;
  sectionId: string;
  onClose: () => void;
  onDirtyChange?: (isDirty: boolean) => void;
}

interface MoveCategoryFormTypes {
  section: string;
}

const UNCATEGORISED = 'Uncategorised';

export const MoveCategoryTrayContent = ({
  menuId,
  itemId,
  sectionId,
  onClose,
  onDirtyChange,
}: MoveCategoryTrayContentProps): ReactElement => {
  const queryClient = useQueryClient();
  const { data: currentMenu } = useMenu(menuId);
  const { showSnackbar } = useSnackbar();

  const sections = useMemo(
    () =>
      flattenCategoryHierarchy(currentMenu.sections)
        .filter((section) => section.sectionId !== sectionId)
        .map((section) => ({
          namesHierarchy: getHierarchyNames(section, currentMenu.sections, {
            id: 'sectionId',
            parent_id: 'parentSectionId',
          }),
          sectionId: section.sectionId,
        })),
    [currentMenu, sectionId],
  );

  const options = [
    ...(sectionId ? [{ value: UNCATEGORISED, name: 'Unassigned' }] : []),
    ...sections.map(({ sectionId: id, namesHierarchy }) => ({
      value: id,
      name: namesHierarchy.slice(0, 2).join(' > '),
    })),
  ];

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting },
  } = useForm<MoveCategoryFormTypes>({
    defaultValues: { section: '' },
    mode: 'onChange',
  });

  const selectValue = watch('section');

  useReportTrayDirty(!!selectValue, onDirtyChange);

  const moveButtonHandler = async (data: MoveCategoryFormTypes) => {
    try {
      const newSectionId = data.section === UNCATEGORISED ? null : data.section;
      let newDisplayOrder = 0;
      if (newSectionId) {
        const selectedSection = currentMenu.sections.find((section) => section.sectionId === newSectionId);
        newDisplayOrder =
          selectedSection.items.length > 0
            ? Math.max(...selectedSection.items.map((item) => item.displayOrder)) + 1
            : 0;
      } else {
        newDisplayOrder =
          currentMenu.uncategorised.length > 0
            ? Math.max(...currentMenu.uncategorised.map((item) => item.displayOrder)) + 1
            : 0;
      }

      await moveMenuItemToSection(menuId, itemId, newSectionId, newDisplayOrder);
      await queryClient.invalidateQueries({ queryKey: [QueryKeys.menu, menuId] });
      onClose();
    } catch {
      showSnackbar({
        status: SnackbarStatus.ERROR,
        message: 'We have been unable to complete your request',
      });
    }
  };

  return (
    <form data-testid="move-menu-item-tray" onSubmit={handleSubmit(moveButtonHandler)} className={styles['content']}>
      <div>
        <Select
          label="Move to section"
          name="move-section"
          options={options}
          value={selectValue}
          onChange={(value) => setValue('section', value, { shouldValidate: true })}
        />
      </div>

      <div className={styles['button-row']}>
        <ButtonContainer
          direction="column-reverse"
          margin="none"
          left={
            <LinkButton type="button" onClick={onClose}>
              Close
            </LinkButton>
          }
          right={
            <PrimaryButton
              size="full-width"
              type="submit"
              loading={isSubmitting}
              disabled={isSubmitting || !selectValue}
            >
              Save
            </PrimaryButton>
          }
        />
      </div>
    </form>
  );
};

export default MoveCategoryTrayContent;
